import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const F = ({ id, label, ...p }) => (
  <div className="space-y-1.5"><Label htmlFor={id}>{label}</Label><Input id={id} data-testid={`ms365-${id}`} {...p} /></div>
);

// Super admin: Microsoft 365 app keys, entered once for all locations (secret stored encrypted, never shown again).
export const Ms365AppCard = ({ onSaved }) => {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { axios.get(`${API}/outlook/app-config`).then((r) => setF({ tenant_id: "", client_id: "", multi_tenant: false, ...r.data, client_secret: "" })).catch(() => setF({})); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/outlook/app-config`, { tenant_id: f.tenant_id, client_id: f.client_id, client_secret: f.client_secret, redirect_uri: f.redirect_uri, multi_tenant: f.multi_tenant });
      toast.success("Microsoft 365 app saved"); setF({ ...f, client_secret: "", secret_set: true, configured: true }); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  if (!f) return null;
  return (
    <Card className="card" data-testid="ms365-app-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg"><KeyRound className="h-6 w-6 text-indigo-600" /></div>
          <div>
            <CardTitle className="text-2xl" style={{ fontFamily: "Manrope" }}>Microsoft 365 App <span className="text-xs align-middle font-bold bg-gray-900 text-white rounded-full px-2 py-0.5 ml-1">SUPER ADMIN</span></CardTitle>
            <CardDescription className="mt-1">Entered once for all locations. Managers just click "Connect Outlook". {f.configured ? <b className="text-emerald-700" data-testid="ms365-configured">Configured ✓</b> : <b className="text-amber-700">Not set up</b>}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <F id="tenant_id" label="Directory (tenant) ID" value={f.tenant_id || ""} onChange={set("tenant_id")} />
          <F id="client_id" label="Application (client) ID" value={f.client_id || ""} onChange={set("client_id")} />
          <F id="client_secret" label={`Client secret value${f.secret_set ? " (saved — leave blank to keep)" : ""}`} type="password" value={f.client_secret} onChange={set("client_secret")} placeholder={f.secret_set ? "••••••••••" : "Paste the secret Value"} />
          <F id="redirect_uri" label="Redirect URI (must be a 'Web' platform URI in Entra)" value={f.redirect_uri || ""} onChange={set("redirect_uri")} />
        </div>
        <label className="flex items-start gap-3 text-sm">
          <Switch checked={!!f.multi_tenant} onCheckedChange={(v) => setF({ ...f, multi_tenant: v })} data-testid="ms365-multi-tenant" />
          <span><b>Allow other companies' Microsoft 365 accounts</b><br /><span className="text-gray-500">Turn on only after the app's "Supported account types" in Entra is set to "Accounts in any organizational directory". Their admin may need to approve once.</span></span>
        </label>
        <Button onClick={save} disabled={saving || !f.tenant_id || !f.client_id || !f.redirect_uri} data-testid="ms365-save-btn">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save</Button>
      </CardContent>
    </Card>
  );
};
